<template>
  <modal
    :show="isVisible"
    :title="modalTitle"
    elem-id="modal_crud_so-service-categories"
    size="small"
    :close-label="t('Cancelar')"
    :save-label="isDeleteMode ? (isDeleting ? t('Deletando') : t('Deletar')) : (isSaving ? t('Salvando') : t('Salvar'))"
    :save-fn="isDeleteMode ? handleDelete : handleSave"
    @close="handleClose"
    @dismiss="handleClose"
  >
    <!-- Content only renders when modal is visible (prevents API calls on mount) -->
    <template v-if="isVisible">
      <!-- Delete Confirmation Mode -->
      <div v-if="isDeleteMode" class="delete-confirmation">
        <p>{{ t('Tem certeza que deseja deletar esta categoria de serviço?') }}</p>
        <p class="text-muted">{{ itemToDelete?.so_service_category_name }}</p>
      </div>

      <!-- Create/Edit Mode -->
      <div v-else>
        <s-o-service-categories-form
          ref="formRef"
          :itemId="currentId"
          :isEditMode="isEditMode"
          @submit-success="handleFormSuccess"
          @submit-error="handleFormError"
        />
        <div v-if="isEditMode" class="delete-action">
          <Button
            data-testid="so-service-categories-delete-button"
            severity="danger"
            outlined
            icon="pi pi-trash"
            :label="t('Deletar')"
            @click="enterDeleteConfirmation"
          />
        </div>
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useSOCatalog } from '../../composables/useSOCatalog';
import { eventBus } from '../../eventBus';
import SOServiceCategoriesForm from './SOServiceCategoriesForm.vue';
import { log, warn as logWarn, error as logError } from '../../utils/DebugLogger';

type Id = number | string;

interface FormInstance {
  loadForEdit: () => Promise<void>;
  handleSubmit: () => Promise<void>;
  resetForm: () => void;
  validateForm: () => boolean;
}

interface DeleteData {
  so_service_category_name?: string;
  [key: string]: unknown;
}

interface ModalOpenPayload {
  id?: Id;
  action?: 'create' | 'edit' | 'delete';
  data?: DeleteData | null;
}

// Get i18n composable
const { t } = useI18n();

// Use composables
const { remove } = useSOCatalog('service_categories');

// Reactive data
const isVisible = ref<boolean>(false);
const isEditMode = ref<boolean>(false);
const isDeleteMode = ref<boolean>(false);
const currentId = ref<Id | null>(null);
const itemToDelete = ref<DeleteData | null>(null);
const formRef = ref<FormInstance | null>(null);
const isSaving = ref<boolean>(false);
const isDeleting = ref<boolean>(false);

// Computed properties
const modalTitle = computed<string>(() => {
  if (isDeleteMode.value) {
    return t('Deletar Categoria de Serviço');
  } else if (isEditMode.value) {
    return t('Editar Categoria de Serviço');
  } else {
    return t('Adicionar Categoria de Serviço');
  }
});

// Methods
const openForCreate = (): void => {
  log('SOServiceCategoriesModal', 'openForCreate', 'Opening modal for create');
  isVisible.value = true;
  isEditMode.value = false;
  isDeleteMode.value = false;
  currentId.value = null;

  // Reset form after modal is visible
  nextTick(() => {
    if (formRef.value) {
      formRef.value.resetForm();
    }
  });
};

const openForEdit = async (id: Id): Promise<void> => {
  log('SOServiceCategoriesModal', 'openForEdit', 'Opening modal for edit', id);
  isVisible.value = true;
  isEditMode.value = true;
  isDeleteMode.value = false;
  currentId.value = id;

  // Wait for the form component to be rendered
  await nextTick();

  // Explicitly load data for edit
  if (formRef.value) {
    await formRef.value.loadForEdit();
  }
};

const openForDelete = async (id: Id, data: DeleteData | null = null): Promise<void> => {
  log('SOServiceCategoriesModal', 'openForDelete', 'Opening modal for delete', id);
  isVisible.value = true;
  isEditMode.value = false;
  isDeleteMode.value = true;
  currentId.value = id;
  itemToDelete.value = data;
};

const enterDeleteConfirmation = (): void => {
  log('SOServiceCategoriesModal', 'enterDeleteConfirmation', 'Switching to delete confirmation', currentId.value);
  isEditMode.value = false;
  isDeleteMode.value = true;
};

const handleClose = (): void => {
  log('SOServiceCategoriesModal', 'handleClose', 'Closing modal');
  isVisible.value = false;
  isEditMode.value = false;
  isDeleteMode.value = false;
  currentId.value = null;
  itemToDelete.value = null;
  isSaving.value = false;
  isDeleting.value = false;

  // Reset form
  if (formRef.value) {
    formRef.value.resetForm();
  }
};

const handleSave = async (): Promise<void> => {
  log('SOServiceCategoriesModal', 'handleSave', 'Save button clicked');
  if (formRef.value) {
    isSaving.value = true;
    try {
      await formRef.value.handleSubmit();
    } finally {
      isSaving.value = false;
    }
  }
};

const handleDelete = async (): Promise<void> => {
  if (!currentId.value) {
    logWarn('SOServiceCategoriesModal', 'handleDelete', 'No ID to delete');
    return;
  }

  log('SOServiceCategoriesModal', 'handleDelete', 'Deleting service category', currentId.value);
  isDeleting.value = true;

  try {
    await remove({ so_service_category_id: currentId.value });
    eventBus.emit('globalMessage.success', t('Categoria de serviço deletada com sucesso'));
    eventBus.emit('so-service-categories.deleted');
    handleClose();
  } catch (error: unknown) {
    logError('SOServiceCategoriesModal', 'handleDelete', 'Error deleting service category', error);
    const message = error instanceof Error ? error.message : String(error);
    eventBus.emit('globalMessage.error', t('Erro ao deletar categoria de serviço') + ': ' + message);
  } finally {
    isDeleting.value = false;
  }
};

const handleFormSuccess = (): void => {
  log('SOServiceCategoriesModal', 'handleFormSuccess', 'Form submitted successfully');
  eventBus.emit('so-service-categories.saved');
  handleClose();
};

const handleFormError = (error: unknown): void => {
  logError('SOServiceCategoriesModal', 'handleFormError', 'Form submission error', error);
  // Error message is already shown by the form component
};

// Standard modal open handler (for modal.so-service-categories.open event)
const handleModalOpen = async (data?: ModalOpenPayload): Promise<void> => {
  log('SOServiceCategoriesModal', 'handleModalOpen', 'Standard modal open event received', data);

  if (data?.action === 'delete' && data?.id) {
    await openForDelete(data.id, data.data || null);
  } else if (data?.id) {
    await openForEdit(data.id);
  } else {
    openForCreate();
  }
};

// Lifecycle - Event listeners
onMounted(() => {
  log('SOServiceCategoriesModal', 'onMounted', 'Component mounted, setting up event listeners');

  // Standard modal events (preferred pattern)
  eventBus.on('modal.so-service-categories.open', handleModalOpen);
  eventBus.on('modal.so-service-categories.close', handleClose);
});

onBeforeUnmount(() => {
  log('SOServiceCategoriesModal', 'onBeforeUnmount', 'Component unmounting, cleaning up event listeners');

  // Remove standard modal events
  eventBus.off('modal.so-service-categories.open', handleModalOpen);
  eventBus.off('modal.so-service-categories.close', handleClose);
});

// Expose methods for parent components (legacy support)
defineExpose({
  openForCreate,
  openForEdit,
  openForDelete
});
</script>

<style scoped>
.delete-confirmation {
  padding: 20px;
  text-align: center;
}

.delete-confirmation p {
  margin-bottom: 10px;
  font-size: 16px;
}

.text-muted {
  color: #6c757d;
  font-style: italic;
}

.delete-action {
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid var(--app-bg-dark, #ccc);
}
</style>
